import React, { useEffect, useRef, useState } from 'react';
import './Home.css';


function CountUpNumber({
  end,
  duration = 1500,
  decimals = 0,
  suffix = '',
  startAnimation = false,
}) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!startAnimation) return;

    let animationFrame;
    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;

      const progress = Math.min((timestamp - startTime) / duration, 1);
      const current = progress * end;
      setCount(current);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      }
    };

    animationFrame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, startAnimation]);

  const formatted =
    decimals > 0
      ? count.toFixed(decimals)
      : Math.floor(count).toLocaleString();

  return (
    <span className="metric-number">
      {formatted}
      {suffix}
    </span>
  );
}

export default function AboutUs() {
  const esgRef = useRef(null);
  const metricsRef = useRef(null);

  const [startEsgCount, setStartEsgCount] = useState(false);
  const [startMetricsCount, setStartMetricsCount] = useState(false);

  useEffect(() => {
    const target = esgRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setStartEsgCount(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const target = metricsRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setStartMetricsCount(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="simple-page">
      <div className="page-inner">
        <p className="section-label">ABOUT US</p>

        <h2>
          <span className="green-text">Sustainable</span>{' '}
          <span className="white-text">Excellence</span>
        </h2>

        <p className="section-text">
          LEEJIN Investment is a Private Equity (P.E.) firm
          established in 2021, serving as a General Partner (GP) in institutional investment structures. We are
          committed to ESG principles, placing strong emphasis on environmental,
          social, and governance values to create long-term sustainable impact.
        </p>

        <p className="section-text">
          We provide investment solutions focused on risk-adjusted returns from a
          long-term perspective. Our goal is to generate stable and sustainable
          cash flows while identifying projects that contribute to a better
          environment and society for future generations.
        </p>

        <p className="section-text">
          Through responsible investment and strategic execution, LEEJIN Investment
          aims to build a sustainable business ecosystem that grows together with
          our clients, local communities, and future generations.
        </p>

        {/* 카드 없는 ESG 강조 */}
        <div className="esg-inline-highlight" ref={esgRef}>
          <div className="esg-inline-label">ESG INVESTMENT</div>
          <div className="esg-inline-ring">
            <span className="esg-inline-number">
              <CountUpNumber
                end={100}
                duration={1500}
                suffix="%"
                startAnimation={startEsgCount}
              />
            </span>
          </div>
          <div className="esg-inline-caption">
            ESG investment accounts for 100% of the total portfolio.
          </div>
        </div>

        {/* 5개 카드 레이아웃 */}
        <div className="metrics-section" ref={metricsRef}>
          <div className="metrics-grid">
            <div className="metric-card">
              
              <span className="metric-label">Wind Power</span>
              <CountUpNumber
                end={79.6}
                duration={1500}
                decimals={1}
                suffix=" MW"
                startAnimation={startMetricsCount}
              />
              <span className="metric-desc">1 Project</span>
            </div>

            <div className="metric-card">
              
              <span className="metric-label">Developer Company</span>
              <CountUpNumber
                end={2}
                duration={1200}
                startAnimation={startMetricsCount}
              />
              <span className="metric-desc">Projects</span>
            </div>

            <div className="metric-card">
              
              <span className="metric-label">Fuel Cell</span>
              <CountUpNumber
                end={36.3}
                duration={1500}
                decimals={1}
                suffix=" MW"
                startAnimation={startMetricsCount}
              />
              <span className="metric-desc">12 Projects</span>
            </div>

            <div className="metric-card">
              
              <span className="metric-label">BESS</span>
              <CountUpNumber
                end={160}
                duration={1500}
                suffix=" MW"
                startAnimation={startMetricsCount}
              />
              <span className="metric-desc">2 Projects</span>
            </div>

            <div className="metric-card">
              
              <span className="metric-label">Solar</span>
              <CountUpNumber
                end={1.5}
                duration={1500}
                decimals={1}
                suffix=" MW"
                startAnimation={startMetricsCount}
              />
              <span className="metric-desc">1 Project</span>
            </div>

            <div className="metric-card">
              
              <span className="metric-label">Substations and TLs</span>
              <CountUpNumber
                end={925.6}
                duration={1500}
                decimals={1}
                suffix=" MW"
                startAnimation={startMetricsCount}
              />
              <span className="metric-desc">3 Projects</span>
            </div>
          </div>
        </div>

        <div className="history-section">
          <h2>History</h2>

          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-dot" />
              <div className="timeline-content">
                <h3>2021</h3>
                <p>
                  Founded LEEJIN Investment and registered as a General Partner (GP)
                  under the Financial Services Commission, South Korea.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot" />
              <div className="timeline-content">
                <h3>2022</h3>
                <p>
                  Completed first advisory engagement for a fuel cell power generation project.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot" />
              <div className="timeline-content">
                <h3>2023</h3>
                <p>
                  Executed first solar power advisory project and initial principal investment (PI).
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot" />
              <div className="timeline-content">
                <h3>2024</h3>
                <p>
                  Signed first wind power acquisition agreement (M&A).
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot" />
              <div className="timeline-content">
                <h3>2025</h3>
                <p>
                  Participated as a project developer in the fisrt ESS power plant project.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}